import ArticleLink from '@/components/feature/ArticleLink';
import { forexBasicsCategories } from '@/mocks/forexBasics';
import SectionHeading from '@/components/base/SectionHeading';
import ScrollReveal from '@/components/base/ScrollReveal';
import { useReadArticleSlugs } from '@/hooks/useLearningProgress';

export default function BasicsCategories() {
  const readSlugs = useReadArticleSlugs();
  const readSet = new Set(readSlugs);

  return (
    <section id="forex-categories" className="scroll-mt-24 bg-background-50">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20 lg:px-10">
        <SectionHeading
          eyebrow="หมวดความรู้"
          title="Forex พื้นฐาน แบ่งเป็น 6 หมวด"
          subtitle="เริ่มจากสองหมวดหลักที่ต้องเข้าใจก่อน แล้วค่อยขยับไปหมวดคำศัพท์ Leverage ต้นทุน และความเสี่ยง แต่ละหัวข้อมีหน้าบทความแยกให้อ่านแบบเจาะลึก"
        />

        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">
          {forexBasicsCategories.map((category, index) => {
            const isFeatured = Boolean(category.featured);

            const readInCategory = category.topics.filter((topic) =>
              readSet.has(topic.to.replace(/^\//, '')),
            ).length;
            const categoryDone = readInCategory >= category.topics.length;

            return (
              <ScrollReveal key={category.id} delay={index * 60} className="h-full">
                <article
                  id={category.id}
                  className={`group flex h-full scroll-mt-24 flex-col rounded-2xl border bg-background-50 p-6 transition-all duration-300 ease-out hover:-translate-y-0.5 md:p-7 ${
                    categoryDone
                      ? 'border-accent-300'
                      : 'border-background-200 hover:border-primary-300'
                  }`}
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                      <h3
                        className={`font-heading font-semibold text-foreground-950 ${
                          isFeatured ? 'text-lg md:text-xl' : 'text-base md:text-lg'
                        }`}
                      >
                        {category.title}
                      </h3>
                      {readInCategory > 0 && (
                        <span
                          className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 font-num text-[11px] font-semibold ${
                            categoryDone
                              ? 'bg-accent-100 text-accent-800'
                              : 'bg-secondary-100 text-secondary-900'
                          }`}
                        >
                          <i className={categoryDone ? 'ri-checkbox-circle-fill' : 'ri-bookmark-line'} />
                          {readInCategory}/{category.topics.length}
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{category.desc}</p>
                  </div>

                  <ul className="mt-5 flex flex-1 flex-col divide-y divide-background-200 border-t border-background-200 pt-1">
                    {category.topics.map((topic, topicIndex) => {
                      const slug = topic.to.replace(/^\//, '');
                      const isRead = readSet.has(slug);
                      return (
                        <li key={topic.to}>
                          <ArticleLink
                            to={topic.to}
                            className="group/topic flex items-center gap-3 py-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
                          >
                            <span
                              className={`flex shrink-0 items-center justify-center font-num font-semibold ${
                                isRead
                                  ? 'h-5 w-5 rounded-full bg-accent-100 text-accent-700'
                                  : `shrink-0 ${isFeatured ? 'text-sm text-primary-400' : 'text-xs text-foreground-400'}`
                              }`}
                            >
                              {isRead ? (
                                <i className="ri-check-line text-xs" />
                              ) : (
                                String(topicIndex + 1).padStart(2, '0')
                              )}
                            </span>
                            <span
                              className={`flex-1 font-medium leading-snug transition-colors group-hover/topic:text-primary-700 ${
                                isRead ? 'text-foreground-500' : 'text-foreground-800'
                              } ${isFeatured ? 'text-sm md:text-[15px]' : 'text-sm'}`}
                            >
                              {topic.label}
                            </span>
                            {isRead ? (
                              <span className="shrink-0 text-[11px] font-medium text-accent-700">อ่านแล้ว</span>
                            ) : (
                              <i className="ri-arrow-right-line shrink-0 text-base text-foreground-300 transition-all group-hover/topic:translate-x-0.5 group-hover/topic:text-primary-600" />
                            )}
                          </ArticleLink>
                        </li>
                      );
                    })}
                  </ul>

                  <span className="mt-5 flex items-center gap-1.5 text-xs font-semibold text-primary-600">
                    <i className="ri-book-open-line text-sm" />
                    {category.topics.length} หัวข้อในหมวดนี้
                  </span>
                </article>
              </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}